// NPM Pacakges
import React, {
  useEffect,
  useState,
  memo,
  useMemo,
  useCallback,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactDataGrid from 'react-data-grid';
import Pagination from 'react-js-pagination';
import {
  Button,
  Card,
  CardBody,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Label,
  Row,
  Col,
  CardHeader,
  UncontrolledButtonDropdown,
  Badge
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faPlus,
  faSyncAlt,
  faColumns,
  faBackspace,
  faBox,
  faMinus,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { DebounceInput } from 'react-debounce-input';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

//Helper file
import utility from '../../utility';
import consts from '../../consts';
import strings from '../../globalization';
import 'react-data-grid/dist/react-data-grid.css';
import './GridBase.css';
import { DynamicModal, UseModal } from '../Model';
import { preferenceForm, preferenceList } from '../../redux/actions';
import { PreferenceForm } from '../../screens/Preference';
import { Modal } from 'reactstrap';
import Preferences from './preferences';
import SortOrderButton from '../SortOrder';

const { routesRegex } = consts;

const Grid = memo(props => {
  const {
    columns,
    action,
    dataProperty,
    showAddButton,
    title,
    baseFilter = {},
    parentRoute,
    detailRoute,
    comboType,
    skipColumn,
    enableDoubleClick,
    disableFilter,
    disableShowHideColumns,
    disableRefresh,
    advanceButtons,
    advanceFilters,
    listId,
    sortColumn,
    gridSortDirection,
    applySearch,
    setApplySearch,
    setAddressId
  } = props;
  const [dropdown, setdDropdown] = useState(false);
  const [enableHeaderFilters, setEnableHeaderFilters] = useState(false);
  const [state, setState] = useState({
    activePage: 1,
    limitPage: consts.defaultPageSize,
    totalItem: 0,
    filters: {},
    sort: {
      column: sortColumn ? sortColumn : '_id',
      direction: gridSortDirection ? 1 : -1
    },
    skipRecord: 0,
    component: {},
    newColumns: [],
    shouldRefresh: true,
    loadPref: props.loadPref || false,
    PrefName,
    sortOrder: props.sortOrder || false
  });

  const [updatedColumns, setNewColumns] = useState([]);
  const dispatch = useDispatch();
  const history = useHistory();
  const { isShowing, toggle } = UseModal();
  const [preferenceMod, setPreferenceMod] = useState({
    visible: false,
    id: null
  });
  const [currentPref, setCurrentPref] = useState(null);
  const [defaultPref, setDefaultPref] = useState(null);
  const [shouldDispatch, setShouldDispatch] = useState(false);

  // Use ref to avoid global variable pollution
  const pageSizeSaveTimeoutRef = useRef(null);

  // Store active preference ID and data in ref to avoid stale closures
  const activePreferenceIdRef = useRef(null);
  const preferenceDataRef = useRef(null);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (pageSizeSaveTimeoutRef.current) {
        clearTimeout(pageSizeSaveTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Handles dispatching preference updates with bundled data
   * @param {Object} params - The parameters for preference update
   * @param {string} params.preferenceId - The ID of the preference to update (optional for new)
   * @param {Object} params.preferenceData - The preference data object
   * @param {Object} params.updatedData - The data to update in the preference
   * @param {string} params.action - The action type ('add' or 'update')
   * @param {boolean} params.silent - Whether to suppress popup messages (default: true)
   */
  const handlePreferenceDispatch = useCallback(
    ({
      preferenceId,
      preferenceData,
      updatedData,
      action = 'update',
      silent = true
    }) => {
      try {
        let prefPayload;

        if (action === 'add') {
          // Create new preference
          prefPayload = {
            GridId: listId,
            PrefName: consts.AUTO_DEFAULT_PREFERENCE_NAME,
            PrefDesc: 'Auto-created default preference',
            PrefVaue: JSON.stringify(updatedData),
            IsDefault: true,
            Controller: '',
            action: 'add',
            silent
          };
        } else {
          // Update existing preference
          const existingPref = preferenceData?.data?.find(
            p => p._id === preferenceId
          );

          if (!existingPref) {
            console.error('Preference not found for update');
            return;
          }

          const existingData = JSON.parse(existingPref.PrefVaue);
          const mergedData = {
            ...existingData,
            ...updatedData
          };

          prefPayload = {
            _id: existingPref._id,
            GridId: listId,
            PrefName: existingPref.PrefName,
            PrefDesc: existingPref.PrefDesc,
            PrefVaue: JSON.stringify(mergedData),
            IsDefault: existingPref.IsDefault,
            Controller: '',
            action: 'update',
            silent
          };
        }

        dispatch(preferenceForm(prefPayload));
      } catch (error) {
        console.error('Failed to dispatch preference:', error);
      }
    },
    [listId, dispatch]
  );

  const gridData = useSelector(state => {
    let updatedState;
    let activePreferenceId = null;
    const { data, comboType, recordCount = 0 } = state[dataProperty].data || {};
    const { isDark } = state.settingData;
    const preferenceData = state.preferenceListData.data || {};

    if (preferenceData.data && preferenceData.data.length) {
      // Priority 1: Look for user's manual default preference (not auto-created)
      let preference = preferenceData.data.find(
        item =>
          item.GridId == listId &&
          item.IsDefault &&
          item.PrefName !== consts.AUTO_DEFAULT_PREFERENCE_NAME
      );

      // Priority 2: Look for any manual preference (not auto-created)
      if (!preference) {
        preference = preferenceData.data.find(
          item =>
            item.GridId == listId &&
            item.PrefName !== consts.AUTO_DEFAULT_PREFERENCE_NAME
        );
      }

      // Priority 3: Use auto-created default as fallback
      if (!preference) {
        preference = preferenceData.data.find(
          item =>
            item.GridId == listId &&
            item.PrefName === consts.AUTO_DEFAULT_PREFERENCE_NAME
        );
      }

      if (preference) {
        updatedState = JSON.parse(preference.PrefVaue);
        updatedState.PrefName = preference.PrefName;
        activePreferenceId = preference._id;
      }
    }

    return {
      data,
      comboType,
      recordCount,
      isDark,
      preferenceData,
      updatedState,
      activePreferenceId
    };
  }, shallowEqual);

  // Update refs whenever gridData changes to avoid stale closures
  // Using gridData itself as dependency to ensure updates happen
  useEffect(() => {
    activePreferenceIdRef.current = gridData.activePreferenceId;
    preferenceDataRef.current = gridData.preferenceData;
  }, [gridData]);
  useEffect(() => {
    let { updatedState, preferenceData } = gridData;
    if (updatedState && shouldRefresh && loadPref) {
      // Hide auto-created preference name from display
      const displayPrefName =
        updatedState.PrefName === consts.AUTO_DEFAULT_PREFERENCE_NAME
          ? ''
          : updatedState.PrefName || '';

      let stateChange = {
        ...state,
        ...updatedState,
        limitPage: updatedState.limitPage || consts.defaultPageSize,
        newColumns: updatedState.columns || [],
        shouldRefresh: false,
        PrefName: displayPrefName
      };

      // Only update columns if they exist in the preference
      if (updatedState.columns && Array.isArray(updatedState.columns)) {
        setNewColumns(updatedState.columns);
      }

      setState(stateChange);

      // Check if filters changed and enable header filters if needed
      if (
        updatedState.filters &&
        Object.keys(updatedState.filters).length > 0 &&
        (!state.filters ||
          JSON.stringify(updatedState.filters) !==
            JSON.stringify(state.filters))
      ) {
        setEnableHeaderFilters(true);
      }

      // gridData.updatedState = {}
      setShouldDispatch(false);
      // Only set addressId if the function exists (specific grids like License)
      if (
        setAddressId &&
        typeof setAddressId === 'function' &&
        preferenceData.data[0]
      ) {
        const obj = JSON.parse(preferenceData.data[0].PrefVaue);
        if (obj.searchValue !== undefined) {
          setAddressId(obj.searchValue);
        }
      }
    } else {
      updatedState = {};
    }
  }, [state, gridData.updatedState]);
  const disablePreference = ['/managePreference', '/history'];
  const {
    activePage,
    limitPage,
    totalItem,
    filters,
    sort,
    skipRecord,
    newColumns,
    shouldRefresh,
    loadPref,
    PrefName
  } = state;
  const { column, direction } = sort;

  // Stable filter change handler using functional state updates
  const onFilterChange = useCallback(event => {
    const { name, value } = event.target;
    const trimmedValue = value.trim();
    setState(prevState => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        [name]: trimmedValue
      },
      activePage: 1,
      skipRecord: 0
    }));

    setShouldDispatch(true);
  }, []); // No dependencies - truly stable
  const boolenRenderer = ({ column, row, rowIdx }) => {
    let val = row[column.key] ? 'Yes' : 'No';
    return <div title={val}>{val}</div>;
  };

  const defaultRenderer = ({ column, row, rowIdx }) => {
    let val = `${row[column.suffix] || ''}${
      column.type === 'number'
        ? row[column.key] || (row[column.key] === 0 ? row[column.key] : '')
        : column.type === 'date' || column.type === 'datetime-local'
        ? row[column.key]
          ? dayjs(row[column.key]).format(
              column?.format
                ? column?.format
                : column.renderer === 'date-time'
                ? consts.MomentFormat.DateTimeFormatAMPM
                : consts.MomentFormat.MonthDateYear
            )
          : ''
        : row[column.key] || ''
    }`;
    return (
      <div
        className={classNames({ 'text-right': column.type === 'number' })}
        title={val}
      >
        {val}
      </div>
    );
  };
  const selectRenderer = ({ column, row, rowIdx }) => {
    const { key, store, type, remoteStore } = column;
    let combo = remoteStore || store;
    if (!Array.isArray(combo)) {
      return <div />;
    }
    let value = row[key] === false ? false : row[key] || [],
      displayValue = [];
    value = Array.isArray(value) ? value : [value];
    value.forEach(val => {
      let filterValue = combo.find(obj => obj.value === val);
      filterValue && displayValue.push(filterValue && filterValue.label);
    });
    let val = displayValue.join(', ');
    return (
      <div
        className={classNames({ 'text-right': type === 'number' })}
        title={val || value[0]}
      >
        {val || value[0]}
      </div>
    );
  };

  // Memoized stable filter renderer components
  const BooleanFilter = useCallback(
    ({ columnKey }) => {
      return (
        <select
          className="form-control"
          name={columnKey}
          onChange={onFilterChange}
          value={filters[columnKey] || ''}
        >
          <option value="">{strings.Yes + '/' + strings.No}</option>
          <option value="true">{strings.Yes}</option>
          <option value="false">{strings.No}</option>
        </select>
      );
    },
    [filters, onFilterChange]
  );

  const DefaultFilter = useCallback(
    ({ columnKey, columnType }) => {
      return (
        <DebounceInput
          type={columnType}
          name={columnKey}
          className="form-control"
          debounceTimeout={800}
          onChange={onFilterChange}
          value={filters[columnKey] || ''}
          forceNotifyByEnter={false}
          forceNotifyOnBlur={false}
        />
      );
    },
    [filters, onFilterChange]
  );

  const SelectFilter = useCallback(
    ({ columnKey, store }) => {
      if (!Array.isArray(store)) {
        return <div />;
      }
      let options = [
        <option key="0" value="">
          {strings.All}
        </option>
      ];
      store.forEach(function(items, index) {
        options.push(
          <option key={index + 1} value={items.value} label={items.label}>
            {items.label}
          </option>
        );
      });
      return (
        <select
          className="form-control"
          name={columnKey}
          onChange={onFilterChange}
          value={filters[columnKey] || ''}
        >
          {options}
        </select>
      );
    },
    [filters, onFilterChange]
  );

  // Cache filter renderer functions to prevent recreation on every render
  const filterRendererCache = useMemo(() => new Map(), []);

  const createFilterRenderer = useCallback(
    (columnKey, filterType, columnType, store) => {
      // Create a unique cache key
      const cacheKey = `${columnKey}-${filterType}-${columnType}`;

      // Return cached function if it exists
      if (filterRendererCache.has(cacheKey)) {
        return filterRendererCache.get(cacheKey);
      }

      // Create and cache the filter renderer function
      let renderer;
      switch (filterType) {
        case 'boolean':
          renderer = () => <BooleanFilter columnKey={columnKey} />;
          break;
        case 'select':
          renderer = () => <SelectFilter columnKey={columnKey} store={store} />;
          break;
        case 'default':
        default:
          renderer = () => (
            <DefaultFilter columnKey={columnKey} columnType={columnType} />
          );
          break;
      }

      filterRendererCache.set(cacheKey, renderer);
      return renderer;
    },
    [BooleanFilter, DefaultFilter, SelectFilter, filterRendererCache]
  );

  // Memoized processColumns function to maintain stable column references
  const processColumns = useCallback(
    ({ columns, skipColumn, comboType = {} }) => {
      return (
        (columns &&
          columns.map(function(col) {
            if (skipColumn && skipColumn[0] === col.key) {
              col.hidden = true;
            }
            col.filterValue = '';
            if (!col.hasOwnProperty('sortable')) {
              col.sortable = true;
            }
            if (!col.hasOwnProperty('resizable')) {
              col.resizable = true;
            }
            if (!col.hasOwnProperty('filterable')) {
              col.filterable = true;
            }
            col.type = col.type || 'text';
            switch (col.type.toLowerCase()) {
              case 'number':
                if (col.filterable && !col.hasOwnProperty('filterRenderer')) {
                  col.filterRenderer = createFilterRenderer(
                    col.key,
                    'default',
                    col.type
                  );
                }
                if (!col.hasOwnProperty('formatter')) {
                  col.formatter = defaultRenderer;
                }
                break;
              case 'bool':
                if (col.filterable && !col.hasOwnProperty('filterRenderer')) {
                  col.filterRenderer = createFilterRenderer(col.key, 'boolean');
                }
                if (!col.hyperlink && !col.hasOwnProperty('formatter')) {
                  col.formatter = boolenRenderer;
                }
                break;
              case 'date':
                if (col.filterable && !col.hasOwnProperty('filterRenderer')) {
                  col.filterRenderer = createFilterRenderer(
                    col.key,
                    'default',
                    col.type
                  );
                }
                if (!col.hasOwnProperty('formatter')) {
                  col.formatter = defaultRenderer;
                }
                break;
              case 'action':
                col.filterable = false;
                col.sortable = false;
                col.resizable = true;
                break;
              default:
                let hasStore = !!col.store;
                if (hasStore && col.mode === 'remote') {
                  col.remoteStore =
                    comboType[col.store] &&
                    utility.remoteComboLoader(col, comboType[col.store]);
                }
                if (col.filterable && !col.hasOwnProperty('filterRenderer')) {
                  const store = col.remoteStore || col.store;
                  col.filterRenderer = hasStore
                    ? createFilterRenderer(col.key, 'select', col.type, store)
                    : createFilterRenderer(col.key, 'default', col.type);
                }
                if (!col.hasOwnProperty('formatter')) {
                  col.formatter = hasStore ? selectRenderer : defaultRenderer;
                }
                col.cellClass = `${col.cellClass ?? ''} no-overflow`.trim();
                break;
            }
            return col;
          })) ||
        []
      );
    },
    [createFilterRenderer]
  );

  const fetchRequest = () => {
    let params = {
      filters: { ...baseFilter, ...filters },
      action: 'list',
      limit: limitPage,
      skip: skipRecord,
      sort: sort.direction !== 'NONE' && {
        [sort.column]: sort.direction === 'ASC' || sort.direction === 1 ? 1 : -1
      }
    };
    if (comboType) {
      params.comboType = comboType;
    }
    if (loadPref) {
      dispatch(
        preferenceList({
          action: 'list',
          limit: 100,
          filters: defaultPref ||
            currentPref || { GridId: props.listId, IsDefault: true }
        })
      );
    }
    action && dispatch(action(params));
  };

  useEffect(() => {
    if (applySearch) {
      fetchRequest();
      setApplySearch(false);
    }
  }, [applySearch]);

  // Debounced fetch for filters - increased timeout for smoother typing
  useEffect(() => {
    if (!shouldDispatch) return;

    const handler = setTimeout(() => {
      fetchRequest();
      setShouldDispatch(false);
    }, 1200);

    return () => clearTimeout(handler);
  }, [state.filters, shouldDispatch]);

  // Immediate fetch for other state parts
  useEffect(() => {
    let shouldLoad = true;

    if (
      parentRoute &&
      window.location.hash.replace(/#\//g, '').toLowerCase() !==
        parentRoute.toLowerCase()
    ) {
      shouldLoad = false;
    }

    if (shouldLoad) {
      fetchRequest();
    }
  }, [
    state.activePage,
    state.limitPage,
    state.sort,
    state.skipRecord,
    state.shouldRefresh,
    state.loadPref,
    state.PrefName,
    state.sortOrder,
    state.newColumns,
    state.component,
    window.location.hash
  ]);

  //handle Add functionality
  const handleAdd = () => {
    if (typeof props.handleAdd === 'function') {
      props.handleAdd(props, state);
    } else {
      history.push({ pathname: `/${parentRoute}/new` });
    }
  };

  //handle Add refresh
  const handleRefresh = () => {
    fetchRequest();
  };

  const rowCount = gridData.recordCount || totalItem;

  /**Custom handeling for Double click using Single click*/
  /************************************/
  let clickedRowInfo = {};
  const onDblClick = (rowIdx, row, column) => {
    if (row && (detailRoute || parentRoute)) {
      if (typeof props.onDblClick === 'function') {
        props.onDblClick(rowIdx, row, column, { detailRoute, parentRoute });
      } else {
        history.push({ pathname: `/${detailRoute || parentRoute}/${row._id}` });
      }
    }
  };

  const onRowClick = (rowIdx, row, column) => {
    const rowClicked = clickedRowInfo,
      clickTime = new Date();
    if (rowIdx === rowClicked.rowIdx) {
      if (clickTime - rowClicked.lastClick <= 300 && enableDoubleClick) {
        onDblClick(rowIdx, row, column);
      }
    }
    clickedRowInfo = { rowIdx, row, column, lastClick: clickTime };
    Object.assign(state, { clickedRowInfo: clickedRowInfo });
  };
  /************************************/

  const onPageChange = currentpage => {
    let count = 0;
    if (currentpage === 1) {
      count = 0;
    } else if (currentpage < activePage) {
      count = skipRecord - limitPage;
    } else {
      count = (currentpage - 1) * limitPage;
    }
    let stateChange = { ...state, activePage: currentpage, skipRecord: count };
    setState(stateChange);
    setShouldDispatch(true);
  };

  /**
   * Handles page size dropdown selection
   * Updates state immediately and saves to preference silently without popup messages
   * @param {Event} event - The click event from dropdown
   */
  const onDropDownSelect = useCallback(
    event => {
      const value = Number(event.target.innerText);

      // Update state immediately for responsive UI
      setState(prevState => ({
        ...prevState,
        activePage: 1,
        limitPage: value,
        skipRecord: 0
      }));
      setShouldDispatch(true);

      // Only save to preference if loadPref is enabled
      if (!listId || !loadPref) {
        return;
      }

      // Capture preference data IMMEDIATELY before any async operations
      const currentActivePreferenceId = activePreferenceIdRef.current;
      const preferenceData = preferenceDataRef.current;

      // Clear any pending save to avoid duplicate saves
      if (pageSizeSaveTimeoutRef.current) {
        clearTimeout(pageSizeSaveTimeoutRef.current);
      }

      // Debounce the save to avoid excessive DB writes (auto-save silently)
      pageSizeSaveTimeoutRef.current = setTimeout(() => {
        const updatedData = {
          limitPage: value,
          activePage: 1,
          skipRecord: 0
        };

        // Check if there's an active preference to update
        if (currentActivePreferenceId) {
          handlePreferenceDispatch({
            preferenceId: currentActivePreferenceId,
            preferenceData,
            updatedData,
            action: 'update',
            silent: true // Auto-save without popup message
          });
        } else {
          // Check if __AutoDefault__ preference exists
          const autoDefaultPref = preferenceData?.data?.find(
            p =>
              p.GridId === listId &&
              p.PrefName === consts.AUTO_DEFAULT_PREFERENCE_NAME
          );

          if (autoDefaultPref) {
            // Update existing __AutoDefault__ preference
            handlePreferenceDispatch({
              preferenceId: autoDefaultPref._id,
              preferenceData,
              updatedData,
              action: 'update',
              silent: true // Auto-save without popup message
            });
          } else {
            // Create new __AutoDefault__ preference with all required data
            const newPrefData = {
              ...updatedData,
              filters: {},
              sort: { column: '_id', direction: -1 }
            };

            handlePreferenceDispatch({
              preferenceData,
              updatedData: newPrefData,
              action: 'add',
              silent: true // Auto-save without popup message
            });
          }
        }
      }, 800); // Reduced timeout for faster auto-save response
    },
    [listId, loadPref, handlePreferenceDispatch]
  );

  const onToggleFilter = () => {
    if (enableHeaderFilters && Object.keys(state.filters).length !== 0) {
      setState({ ...state, filters: {} });
    }
    setEnableHeaderFilters(!enableHeaderFilters);
  };

  const getSortedColumns = (column, direction) => {
    let stateChange = { ...state, sort: { column, direction } };
    setState(stateChange);
    setShouldDispatch(true);
  };

  const pageSizeToggle = () => {
    setdDropdown(!dropdown);
  };

  // Optimized columnArr memoization - only recreate when essential dependencies change
  const columnArr = useMemo(() => {
    let columnFromPref = utility.cloneArrayOfObject(columns);
    let newCols =
      updatedColumns && updatedColumns.length > 0 ? updatedColumns : newColumns;
    if (newCols && newCols.length > 0) {
      columnFromPref = columnFromPref.map(item => {
        let newColumn = newCols.find(newCol => newCol.key === item.key);
        return { ...item, ...newColumn };
      });
    }
    return processColumns({
      columns: columnFromPref,
      comboType: gridData.comboType,
      skipColumn
    });
  }, [
    columns,
    gridData.comboType,
    updatedColumns,
    newColumns,
    skipColumn,
    processColumns
  ]);
  const showHideColumns = (event, name, checked) => {
    let colIndex = columnArr.findIndex(e => e.key === name);
    columnArr[colIndex].hidden = !checked;
    const col = utility.cloneArrayOfObject(columnArr);
    setNewColumns(Object.values({ ...updatedColumns, ...col }));
  };
  const getDefaultColumns = props => {
    let toReturn;
    return (toReturn = (
      <DropdownMenu style={{ overflowY: 'auto', maxHeight: '720px' }}>
        {columnArr.map((obj, index) => {
          let isDisabled = index === 0 ? true : false;
          return (
            <DropdownItem
              disabled={isDisabled}
              key={index}
              onClick={e => showHideColumns(e, obj.key, obj.hidden)}
              toggle={false}
            >
              <input type="checkbox" readOnly={true} checked={!obj.hidden} />{' '}
              {obj.name}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    ));
  };

  const managePreference = () => {
    history.push('/managePreference');
  };

  const addPreference = id => {
    setPreferenceMod({ visible: !preferenceMod.visible, id });
  };

  const getPreferences = props => {
    let { Preference = [] } = gridData.comboType || {},
      toReturn,
      menus;
    return (toReturn = (
      <DropdownMenu>
        {
          <DropdownItem
            key={101}
            onClick={() => addPreference('new')}
            toggle={false}
          >
            {strings.AddPreference}
          </DropdownItem>
        }
        <hr />
        <Preferences
          dropDownItemClick={obj => {
            const prefData = JSON.parse(obj.PrefVaue);
            state.loadPref = true;
            state.shouldRefresh = true;
            dispatch(
              preferenceList({
                action: 'list',
                limit: 100,
                filters: { GridId: props.listId, _id: obj._id }
              })
            );
            if (setAddressId && typeof setAddressId === 'function') {
              setAddressId(prefData.searchValue || '');
            }
            if (setApplySearch && typeof setApplySearch === 'function') {
              setApplySearch(true);
            }
            setCurrentPref({ GridId: props.listId, _id: obj._id });
            obj.IsDefault &&
              setDefaultPref({ GridId: props.listId, _id: obj._id });
          }}
          addPreference={addPreference}
          listId={props.listId}
          onRemovePref={onRemovePref}
        />
      </DropdownMenu>
    ));
  };

  const onRemovePref = () => {
    let stateChange = {
      ...state,
      activePage: 1,
      limitPage: consts.defaultPageSize,
      totalItem: 0,
      filters: {},
      sort: { column: '_id', direction: -1 },
      skipRecord: 0,
      component: {},
      newColumns: [],
      shouldRefresh: false,
      PrefName: ''
    };
    if (setAddressId && typeof setAddressId === 'function') {
      setAddressId('');
    }
    if (setApplySearch && typeof setApplySearch === 'function') {
      setApplySearch(true);
    }
    setState(stateChange);
    setNewColumns(utility.cloneArrayOfObject(columns));
    setEnableHeaderFilters(false);
  };

  const handleDelete = () => {
    let { rowIdx, row, column } = state.clickedRowInfo;
    dispatch(preferenceForm({ action: 'delete', _id: row._id }));
  };

  return (
    <Card
      className={classNames('custom-card', {
        'custom-card-dark': gridData.isDark
      })}
    >
      <CardHeader
        className={classNames('custom-header', {
          'custom-header-dark': gridData.isDark
        })}
      >
        <Row sm="12" md="12" lg="12">
          <Col sm="8" md="8" lg="10">
            <Label className="text-white">
              <h4>
                {title}{' '}
                <small>
                  {PrefName}
                  {PrefName && (
                    <Badge onClick={() => onRemovePref()}>
                      <FontAwesomeIcon icon={faTimes} />
                    </Badge>
                  )}{' '}
                </small>{' '}
              </h4>
            </Label>
          </Col>
          <Col sm="4" md="4" lg="2">
            {advanceButtons}
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {(isShowing || props.isShowing) && (
          <div className="Dynamic-Model">
            <DynamicModal
              isShowing={isShowing}
              hide={toggle}
              scope={props}
              component={props.component || state.component}
            />
          </div>
        )}
        <Modal isOpen={preferenceMod.visible} size="xl">
          <PreferenceForm
            {...props}
            state={state}
            updatedColumns={updatedColumns}
            hide={PrefName => {
              setPreferenceMod({ visible: !preferenceMod.visible, id: null });
              if (PrefName && !state.PrefName) setState({ ...state, PrefName });
            }}
            id={preferenceMod.id}
          />
        </Modal>
        <Row sm="12" md="12" lg="12" className="mb-2">
          <div
            className="btn-toolbar"
            role="toolbar"
            aria-label="Toolbar with button groups"
          >
            <div
              className="btn-group mr-1"
              role="group"
              aria-label="First group"
            >
              {!disableFilter && (
                <Button
                  title={strings.FilterText}
                  type="button"
                  color="secondary"
                  onClick={onToggleFilter}
                >
                  {' '}
                  <FontAwesomeIcon icon={faFilter}></FontAwesomeIcon>{' '}
                  {strings.FilterText}
                </Button>
              )}
            </div>
            <div
              className="btn-group mr-1"
              role="group"
              aria-label="First group"
            >
              {showAddButton && (
                <Button
                  title={strings.Add}
                  onClick={handleAdd}
                  color="primary"
                  disabled={props.disableAdd}
                >
                  <FontAwesomeIcon
                    className="ls"
                    icon={props.addIcon || faPlus}
                  ></FontAwesomeIcon>{' '}
                  {props.addText || strings.Add}
                </Button>
              )}
            </div>
            <div
              className="btn-group mr-1"
              role="group"
              aria-label="First group"
            >
              {!disableShowHideColumns && (
                <UncontrolledButtonDropdown>
                  <DropdownToggle title={strings.Column} caret color="info">
                    <FontAwesomeIcon
                      className="ls"
                      icon={faColumns}
                    ></FontAwesomeIcon>{' '}
                    {strings.Column}
                  </DropdownToggle>
                  {getDefaultColumns(props)}
                </UncontrolledButtonDropdown>
              )}
            </div>
            {props.loadPref && (
              <div
                className="btn-group mr-1"
                role="group"
                aria-label="First group"
              >
                <UncontrolledButtonDropdown>
                  <DropdownToggle
                    title={strings.Preferences}
                    caret
                    color="info"
                  >
                    <FontAwesomeIcon
                      className="ls"
                      icon={faBox}
                    ></FontAwesomeIcon>{' '}
                    {strings.Preferences}
                  </DropdownToggle>
                  {getPreferences(props)}
                </UncontrolledButtonDropdown>
              </div>
            )}
            {props.sortOrder && props.listId && (
              <div
                className="btn-group mr-1"
                role="group"
                aria-label="First group"
              >
                <SortOrderButton
                  columns={columns}
                  currentSort={state.sort}
                  onSortChange={sort => setState({ ...state, sort })}
                  listId={listId}
                  dispatch={dispatch}
                />
              </div>
            )}
            {!props.disableDelete && (
              <div
                className="btn-group mr-1"
                role="group"
                aria-label="First group"
              >
                <Button
                  title={strings.Delete}
                  onClick={handleDelete}
                  color="danger"
                  disabled={props.disableAdd}
                >
                  <FontAwesomeIcon
                    className="ls"
                    icon={faMinus}
                  ></FontAwesomeIcon>{' '}
                  {strings.Delete}
                </Button>
              </div>
            )}
            {props.CustomButtons}
          </div>
        </Row>

        {advanceFilters ? (
          <Row sm="12" md="12" lg="12">
            {advanceFilters}
          </Row>
        ) : null}
        <ReactDataGrid
          columns={columnArr.filter(data => !data.hidden)}
          rows={gridData.data || []}
          enableFilters={enableHeaderFilters}
          onSort={getSortedColumns}
          sortColumn={column}
          sortDirection={direction}
          onRowClick={onRowClick}
          initialState={{
            sorting: {
              model: [{ field: 'updatedAt', sort: 'asc' }]
            }
          }}
          {...(props.style
            ? { style: props.style }
            : { height: props.height || 550 })}
        />
        <div
          className="row"
          style={{
            display: 'flex',
            paddingTop: '1em',
            justifyContent: 'space-between'
          }}
        >
          <div
            className={classNames('col-xs-12 text-black', {
              'text-white': gridData.isDark
            })}
          >
            <span>
              {' '}
              {strings.DisplayingRecord}{' '}
              {rowCount === 0
                ? 0
                : rowCount <= limitPage
                ? 1
                : rowCount > limitPage
                ? limitPage * activePage - limitPage + 1
                : 1}{' '}
              to{' '}
              {rowCount <= limitPage
                ? rowCount
                : rowCount > limitPage
                ? limitPage * activePage > rowCount
                  ? rowCount
                  : limitPage * activePage
                : limitPage}{' '}
              of {rowCount}
            </span>
          </div>
          <div className="col-xs-12" style={{ display: 'flex' }}>
            {!disableRefresh && (
              <div className="refreshbtn">
                <Button
                  color="primary"
                  title={strings.Refresh}
                  onClick={handleRefresh}
                >
                  <span>
                    <FontAwesomeIcon icon={faSyncAlt} />
                  </span>
                </Button>
              </div>
            )}
            <Pagination
              innerClass="blogListPageInner"
              activePage={activePage}
              itemsCountPerPage={limitPage}
              totalItemsCount={rowCount}
              pageRangeDisplayed={5}
              onChange={onPageChange}
            />
            <ButtonDropdown
              title={strings.RowsPerPage}
              direction="up"
              isOpen={dropdown}
              toggle={pageSizeToggle}
              className="toolBtn"
            >
              <DropdownToggle caret size="sm" color="primary">
                {limitPage}
              </DropdownToggle>
              <DropdownMenu className="dropdown-width">
                {consts.DropdownMenuOptions.map(function(item, index) {
                  return (
                    <DropdownItem key={index} onClick={onDropDownSelect}>
                      {item.value}
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </ButtonDropdown>
          </div>
        </div>
      </CardBody>
    </Card>
  );
});
Grid.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  action: PropTypes.func.isRequired,
  dataProperty: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  showAddButton: PropTypes.bool,
  baseFilter: PropTypes.object,
  parentRoute: PropTypes.string,
  disableFilter: PropTypes.bool,
  disableShowHideColumns: PropTypes.bool,
  disableDelete: PropTypes.bool,
  enableDoubleClick: PropTypes.bool
};
Grid.defaultProps = {
  showAddButton: true,
  disableFilter: false,
  disableShowHideColumns: false,
  disableDelete: true,
  CustomButtons: <></>,
  disableRefresh: true,
  enableDoubleClick: true
};

function GridBase(Component) {
  class EnhancedGrid extends React.PureComponent {
    render = () => <Component {...this.props} />;
  }
  EnhancedGrid.propTypes = Component.propTypes;
  return EnhancedGrid;
}
export default GridBase(Grid);
